import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Building2, Search, Edit, Trash2, MapPin, GitCompare, Send, X, Phone, Mail, MessageSquare, Filter, ChevronDown, ChevronUp, AlertCircle, Copy, User } from "lucide-react";
import { toast } from "sonner";
import { INDIAN_STATES, CITIES_BY_STATE, POSSESSION_STATUS_OPTIONS, FURNISHING_OPTIONS, getStateLabel, getCityLabel } from "@/data/indianLocations";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Properties() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterPurpose, setFilterPurpose] = useState("all");
  const [deleteId, setDeleteId] = useState(null);
  
  // Advanced search filters state - India specific
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filterState, setFilterState] = useState("all");
  const [filterCity, setFilterCity] = useState("all");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterBudgetMin, setFilterBudgetMin] = useState("");
  const [filterBudgetMax, setFilterBudgetMax] = useState("");
  const [filterBHK, setFilterBHK] = useState("all");
  const [filterAreaMin, setFilterAreaMin] = useState("");
  const [filterAreaMax, setFilterAreaMax] = useState("");
  const [filterPossession, setFilterPossession] = useState("all");
  const [filterFurnishing, setFilterFurnishing] = useState("all");
  const [filterRera, setFilterRera] = useState("all");
  
  // Location disambiguation state
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [matchingLocations, setMatchingLocations] = useState([]);
  const [minCriteriaError, setMinCriteriaError] = useState("");
  
  // Popular locations from analytics
  const [popularLocations, setPopularLocations] = useState([]);
  
  // Inquiry state
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [inquiryLoading, setInquiryLoading] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    message: "",
    budget_min: "",
    budget_max: "",
    preferred_contact: "phone"
  });
  
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isGuest = user.role === "guest";
  const isRegularUser = user.role === "user";
  const isSourcingManager = user.role === "sourcing_manager";
  const isAdmin = user.role === "admin";
  const isViewOnlyUser = isGuest || isRegularUser; // Both guest and user have same view-only access
  const canManageProperties = !isViewOnlyUser; // Only admin, agents, sourcing managers can manage
  const canDeleteProperties = canManageProperties && !isSourcingManager; // Sourcing managers cannot delete

  // CAPIN Logo URL for user/guest view
  const CAPIN_LOGO = "https://customer-assets.emergentagent.com/job_4901b1f7-f613-488e-b000-d3e46b5149ad/artifacts/g01ubmeo_IMG_20250901_221756_327.webp";

  // Fetch popular locations from search analytics
  const fetchPopularLocations = async () => {
    try {
      const token = localStorage.getItem("token");
      // Get unique locations from properties with state/city info
      const uniqueLocations = [];
      const seenLocations = new Set();
      
      properties.forEach(prop => {
        if (prop.city && prop.state && prop.location) {
          const key = `${prop.location}-${prop.city}-${prop.state}`;
          if (!seenLocations.has(key)) {
            seenLocations.add(key);
            uniqueLocations.push({
              location: prop.location,
              city: prop.city,
              state: prop.state,
              displayName: `${prop.location}, ${getCityLabel(prop.state, prop.city)}`
            });
          }
        }
      });
      
      // Take top 6 unique locations
      setPopularLocations(uniqueLocations.slice(0, 6));
    } catch (error) {
      console.log("Could not fetch popular locations");
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  // Refetch when sourcing manager changes context
  useEffect(() => {
    const handler = () => fetchProperties();
    window.addEventListener('context-changed', handler);
    return () => window.removeEventListener('context-changed', handler);
  }, []);

  useEffect(() => {
    filterProperties();
  }, [properties, searchTerm, filterType, filterPurpose, filterState, filterCity, filterLocation, filterBudgetMin, filterBudgetMax, filterBHK, filterAreaMin, filterAreaMax, filterPossession, filterFurnishing, filterRera]);

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let data = response.data;
      // Sourcing Manager: only show their own properties
      if (isSourcingManager && user.id) {
        data = data.filter(p => p.created_by === user.id || p.added_by === user.id);
      }
      setProperties(data);
    } catch (error) {
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  // Update popular locations when properties change
  useEffect(() => {
    if (properties.length > 0) {
      fetchPopularLocations();
    }
  }, [properties]);

  // Clone a property
  const handleCloneProperty = async (propertyId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/properties/${propertyId}/clone`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Property cloned! Redirecting to edit...");
      navigate(`/properties/edit/${res.data.new_property_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clone property");
    }
  };

  // Count active criteria for minimum 2 validation
  const countActiveCriteria = () => {
    let count = 0;
    if (filterState !== "all") count++;
    if (filterCity !== "all") count++;
    if (filterLocation) count++;
    if (filterBudgetMin || filterBudgetMax) count++;
    if (filterBHK !== "all") count++;
    if (filterAreaMin || filterAreaMax) count++;
    if (filterPossession !== "all") count++;
    if (filterFurnishing !== "all") count++;
    if (filterRera !== "all") count++;
    if (filterType !== "all") count++;
    if (filterPurpose !== "all") count++;
    if (searchTerm) count++;
    return count;
  };

  // Check if location exists in multiple cities
  const checkLocationAmbiguity = (location) => {
    if (!location || filterCity !== "all") return;
    
    const locationLower = location.toLowerCase();
    const matchingCityStates = [];
    
    properties.forEach(prop => {
      if (prop.location && prop.location.toLowerCase().includes(locationLower)) {
        if (prop.city && prop.state) {
          const key = `${prop.state}-${prop.city}`;
          if (!matchingCityStates.find(m => `${m.state}-${m.city}` === key)) {
            matchingCityStates.push({ state: prop.state, city: prop.city });
          }
        }
      }
    });
    
    if (matchingCityStates.length > 1) {
      setMatchingLocations(matchingCityStates);
      setShowLocationPrompt(true);
    }
  };

  const filterProperties = () => {
    let filtered = properties;

    // Basic search (title and location)
    if (searchTerm) {
      filtered = filtered.filter(
        (p) =>
          p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (p.city && p.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (p.state && p.state.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Property type filter
    if (filterType !== "all") {
      filtered = filtered.filter((p) => p.property_type === filterType);
    }

    // Purpose filter
    if (filterPurpose !== "all") {
      filtered = filtered.filter((p) => p.purpose === filterPurpose);
    }

    // State filter
    if (filterState !== "all") {
      filtered = filtered.filter((p) => p.state === filterState);
    }

    // City filter
    if (filterCity !== "all") {
      filtered = filtered.filter((p) => p.city === filterCity);
    }

    // Location/Area filter (locality search)
    if (filterLocation) {
      filtered = filtered.filter((p) =>
        p.location.toLowerCase().includes(filterLocation.toLowerCase())
      );
    }

    // Budget filter
    if (filterBudgetMin) {
      const minBudget = parseFloat(filterBudgetMin);
      filtered = filtered.filter((p) => p.price >= minBudget);
    }
    if (filterBudgetMax) {
      const maxBudget = parseFloat(filterBudgetMax);
      filtered = filtered.filter((p) => p.price <= maxBudget);
    }

    // BHK filter (only for residential)
    if (filterBHK !== "all") {
      const bhkValue = parseInt(filterBHK);
      filtered = filtered.filter((p) => {
        if (p.property_type !== "residential") return true;
        return p.bedrooms === bhkValue;
      });
    }

    // Area filter (sq ft)
    if (filterAreaMin) {
      const minArea = parseFloat(filterAreaMin);
      filtered = filtered.filter((p) => {
        const area = parseFloat(p.area) || 0;
        return area >= minArea;
      });
    }
    if (filterAreaMax) {
      const maxArea = parseFloat(filterAreaMax);
      filtered = filtered.filter((p) => {
        const area = parseFloat(p.area) || 0;
        return area <= maxArea;
      });
    }

    // Possession status filter
    if (filterPossession !== "all") {
      filtered = filtered.filter((p) => p.possession_status === filterPossession);
    }

    // Furnishing filter
    if (filterFurnishing !== "all") {
      filtered = filtered.filter((p) => p.furnishing === filterFurnishing);
    }

    // RERA filter
    if (filterRera !== "all") {
      filtered = filtered.filter((p) => {
        if (filterRera === "yes") return p.rera_registered === true;
        if (filterRera === "no") return p.rera_registered === false || !p.rera_registered;
        return true;
      });
    }

    setFilteredProperties(filtered);
  };

  // Clear all advanced filters
  const clearAdvancedFilters = () => {
    setFilterState("all");
    setFilterCity("all");
    setFilterLocation("");
    setFilterBudgetMin("");
    setFilterBudgetMax("");
    setFilterBHK("all");
    setFilterAreaMin("");
    setFilterAreaMax("");
    setFilterPossession("all");
    setFilterFurnishing("all");
    setFilterRera("all");
    setMinCriteriaError("");
  };

  // Check if any advanced filter is active
  const hasActiveAdvancedFilters = filterState !== "all" || filterCity !== "all" || filterLocation || filterBudgetMin || filterBudgetMax || filterBHK !== "all" || filterAreaMin || filterAreaMax || filterPossession !== "all" || filterFurnishing !== "all" || filterRera !== "all";

  // Log search for analytics
  const logSearch = async (term, filters, resultsCount) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/search-logs`, {
        search_term: term || null,
        filters: filters,
        results_count: resultsCount
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.log("Search log error (non-critical):", error);
    }
  };

  // Debounced search logging
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm || filterType !== "all" || filterPurpose !== "all" || hasActiveAdvancedFilters) {
        const filters = {
          property_type: filterType !== "all" ? filterType : null,
          purpose: filterPurpose !== "all" ? filterPurpose : null,
          state: filterState !== "all" ? filterState : null,
          city: filterCity !== "all" ? filterCity : null,
          location: filterLocation || null,
          budget_min: filterBudgetMin || null,
          budget_max: filterBudgetMax || null,
          bhk: filterBHK !== "all" ? filterBHK : null,
          area_min: filterAreaMin || null,
          area_max: filterAreaMax || null,
          possession_status: filterPossession !== "all" ? filterPossession : null,
          furnishing: filterFurnishing !== "all" ? filterFurnishing : null,
          rera_approved: filterRera !== "all" ? filterRera : null
        };
        // Remove null values
        Object.keys(filters).forEach(key => filters[key] === null && delete filters[key]);
        logSearch(searchTerm, filters, filteredProperties.length);
      }
    }, 2000); // Log after 2 seconds of inactivity

    return () => clearTimeout(timer);
  }, [searchTerm, filterType, filterPurpose, filterState, filterCity, filterLocation, filterBudgetMin, filterBudgetMax, filterBHK, filterAreaMin, filterAreaMax, filterPossession, filterFurnishing, filterRera, filteredProperties.length]);

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/properties/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Property deleted successfully");
      setProperties(properties.filter((p) => p.id !== deleteId));
      setDeleteId(null);
    } catch (error) {
      toast.error("Failed to delete property");
    }
  };

  // Property selection for inquiry
  const togglePropertySelection = (propertyId) => {
    setSelectedProperties(prev => 
      prev.includes(propertyId) 
        ? prev.filter(id => id !== propertyId)
        : [...prev, propertyId]
    );
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    
    if (selectedProperties.length === 0) {
      toast.error("Please select at least one property");
      return;
    }
    
    setInquiryLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/inquiries/submit`,
        {
          ...inquiryForm,
          property_ids: selectedProperties,
          budget_min: inquiryForm.budget_min ? parseFloat(inquiryForm.budget_min) : null,
          budget_max: inquiryForm.budget_max ? parseFloat(inquiryForm.budget_max) : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowInquiryModal(false);
      setSelectedProperties([]);
      setInquiryForm({
        full_name: "",
        email: "",
        phone: "",
        message: "",
        budget_min: "",
        budget_max: "",
        preferred_contact: "phone"
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit inquiry");
    } finally {
      setInquiryLoading(false);
    }
  };

  const getSelectedPropertyDetails = () => {
    return properties.filter(p => selectedProperties.includes(p.id));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0f2744] border-t-transparent" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="fade-in space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#0f2744] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Properties
            </h1>
            <p className="text-slate-500 mt-1">
              {canManageProperties ? "Manage your property listings" : "Browse available properties"}
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="compare-properties-button"
              onClick={() => navigate("/properties/compare")}
              variant="outline"
              className="border-[#0f2744] text-[#0f2744] hover:bg-[#0f2744]/5 rounded-lg"
            >
              <GitCompare className="h-5 w-5 mr-2" />
              Compare
            </Button>
          </div>
        </div>

        {/* Filters Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          {/* Basic Filters Row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                data-testid="search-properties-input"
                type="text"
                placeholder="Search properties..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 rounded-lg border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0f2744] focus:ring-[#0f2744]"
              />
            </div>
            <select
              data-testid="filter-type-select"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="h-11 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm focus:bg-white focus:border-[#0f2744] focus:ring-1 focus:ring-[#0f2744] cursor-pointer"
            >
              <option value="all">All Types</option>
              <option value="residential">Residential</option>
              <option value="commercial">Commercial</option>
              <option value="land">Land</option>
              <option value="mixed">Mixed</option>
            </select>
            <select
              data-testid="filter-purpose-select"
              value={filterPurpose}
              onChange={(e) => setFilterPurpose(e.target.value)}
              className="h-11 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm focus:bg-white focus:border-[#0f2744] focus:ring-1 focus:ring-[#0f2744] cursor-pointer"
            >
              <option value="all">All Purposes</option>
              <option value="sale">Sale</option>
              <option value="lease">Lease</option>
              <option value="rent">Rent</option>
              <option value="bts">BTS (Built-to-Suit)</option>
              <option value="purchase">Purchase</option>
            </select>
            <Button
              data-testid="toggle-advanced-filters"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              variant="outline"
              className={`h-11 rounded-lg border-slate-200 ${hasActiveAdvancedFilters ? 'border-[#c9a227] bg-[#fffdf5] text-[#c9a227]' : ''}`}
            >
              <Filter className="h-4 w-4 mr-2" />
              Advanced
              {hasActiveAdvancedFilters && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-[#c9a227] text-white rounded-full">
                  {[filterLocation, filterBudgetMin, filterBudgetMax, filterBHK !== "all" ? filterBHK : null, filterAreaMin, filterAreaMax].filter(Boolean).length}
                </span>
              )}
              {showAdvancedFilters ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
            </Button>
          </div>

          {/* Popular Locations Quick Select */}
          {popularLocations.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-slate-500">Popular Locations:</span>
                {popularLocations.map((loc, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setFilterState(loc.state);
                      setFilterCity(loc.city);
                      setFilterLocation(loc.location);
                      setShowAdvancedFilters(true);
                    }}
                    className="px-3 py-1.5 text-xs bg-gradient-to-r from-[#0f2744] to-[#1e3a5f] text-white rounded-full hover:from-[#1e3a5f] hover:to-[#2a4a6f] transition-all shadow-sm hover:shadow-md"
                    data-testid={`popular-location-${idx}`}
                  >
                    <MapPin className="h-3 w-3 inline mr-1" />
                    {loc.displayName}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Advanced Filters Panel */}
          {showAdvancedFilters && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-[#0f2744]">Advanced Search Filters (India)</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Select minimum 2 criteria for accurate results</p>
                </div>
                {hasActiveAdvancedFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAdvancedFilters}
                    className="text-slate-500 hover:text-[#0f2744]"
                    data-testid="clear-advanced-filters"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Clear All
                  </Button>
                )}
              </div>

              {/* Minimum criteria warning */}
              {countActiveCriteria() === 1 && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <p className="text-xs text-amber-800">Please select at least one more filter criteria for better search results</p>
                </div>
              )}
              
              {/* Row 1: State, City, Location */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {/* State Filter */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1">State</Label>
                  <select
                    data-testid="filter-state-select"
                    value={filterState}
                    onChange={(e) => {
                      setFilterState(e.target.value);
                      setFilterCity("all"); // Reset city when state changes
                    }}
                    className="w-full h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm focus:bg-white focus:border-[#0f2744] focus:ring-1 focus:ring-[#0f2744] cursor-pointer"
                  >
                    <option value="all">All States</option>
                    {INDIAN_STATES.map(state => (
                      <option key={state.value} value={state.value}>{state.label}</option>
                    ))}
                  </select>
                </div>

                {/* City Filter */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1">City</Label>
                  <select
                    data-testid="filter-city-select"
                    value={filterCity}
                    onChange={(e) => setFilterCity(e.target.value)}
                    className="w-full h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm focus:bg-white focus:border-[#0f2744] focus:ring-1 focus:ring-[#0f2744] cursor-pointer"
                    disabled={filterState === "all"}
                  >
                    <option value="all">{filterState === "all" ? "Select State First" : "All Cities"}</option>
                    {filterState !== "all" && CITIES_BY_STATE[filterState]?.map(city => (
                      <option key={city.value} value={city.value}>{city.label}</option>
                    ))}
                  </select>
                </div>

                {/* Area/Locality Filter */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1">Area / Locality</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      data-testid="filter-location-input"
                      type="text"
                      placeholder="e.g., Baner, Wakad, Koramangala..."
                      value={filterLocation}
                      onChange={(e) => {
                        setFilterLocation(e.target.value);
                        checkLocationAmbiguity(e.target.value);
                      }}
                      className="pl-9 h-10 rounded-lg border-slate-200 bg-slate-50 focus:bg-white text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Row 2: Budget, BHK, Area */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {/* Budget Range Filter */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1">Budget Range (₹)</Label>
                  <div className="flex gap-2">
                    <Input
                      data-testid="filter-budget-min"
                      type="number"
                      placeholder="Min"
                      value={filterBudgetMin}
                      onChange={(e) => setFilterBudgetMin(e.target.value)}
                      className="h-10 rounded-lg border-slate-200 bg-slate-50 focus:bg-white text-sm"
                    />
                    <Input
                      data-testid="filter-budget-max"
                      type="number"
                      placeholder="Max"
                      value={filterBudgetMax}
                      onChange={(e) => setFilterBudgetMax(e.target.value)}
                      className="h-10 rounded-lg border-slate-200 bg-slate-50 focus:bg-white text-sm"
                    />
                  </div>
                </div>

                {/* BHK Filter */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1">BHK Configuration</Label>
                  <select
                    data-testid="filter-bhk-select"
                    value={filterBHK}
                    onChange={(e) => setFilterBHK(e.target.value)}
                    className="w-full h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm focus:bg-white focus:border-[#0f2744] focus:ring-1 focus:ring-[#0f2744] cursor-pointer"
                  >
                    <option value="all">All BHK</option>
                    <option value="1">1 BHK</option>
                    <option value="2">2 BHK</option>
                    <option value="3">3 BHK</option>
                    <option value="4">4 BHK</option>
                    <option value="5">5+ BHK</option>
                  </select>
                </div>

                {/* Area Range Filter */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1">Area Range (sq.ft)</Label>
                  <div className="flex gap-2">
                    <Input
                      data-testid="filter-area-min"
                      type="number"
                      placeholder="Min"
                      value={filterAreaMin}
                      onChange={(e) => setFilterAreaMin(e.target.value)}
                      className="h-10 rounded-lg border-slate-200 bg-slate-50 focus:bg-white text-sm"
                    />
                    <Input
                      data-testid="filter-area-max"
                      type="number"
                      placeholder="Max"
                      value={filterAreaMax}
                      onChange={(e) => setFilterAreaMax(e.target.value)}
                      className="h-10 rounded-lg border-slate-200 bg-slate-50 focus:bg-white text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Row 3: Possession, Furnishing, RERA */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Possession Status Filter */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1">Possession Status</Label>
                  <select
                    data-testid="filter-possession-select"
                    value={filterPossession}
                    onChange={(e) => setFilterPossession(e.target.value)}
                    className="w-full h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm focus:bg-white focus:border-[#0f2744] focus:ring-1 focus:ring-[#0f2744] cursor-pointer"
                  >
                    <option value="all">All</option>
                    {POSSESSION_STATUS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Furnishing Filter */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1">Furnishing</Label>
                  <select
                    data-testid="filter-furnishing-select"
                    value={filterFurnishing}
                    onChange={(e) => setFilterFurnishing(e.target.value)}
                    className="w-full h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm focus:bg-white focus:border-[#0f2744] focus:ring-1 focus:ring-[#0f2744] cursor-pointer"
                  >
                    <option value="all">All</option>
                    {FURNISHING_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* RERA Approved Filter */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1">RERA Approved</Label>
                  <select
                    data-testid="filter-rera-select"
                    value={filterRera}
                    onChange={(e) => setFilterRera(e.target.value)}
                    className="w-full h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm focus:bg-white focus:border-[#0f2744] focus:ring-1 focus:ring-[#0f2744] cursor-pointer"
                  >
                    <option value="all">All</option>
                    <option value="yes">RERA Approved</option>
                    <option value="no">Not RERA Approved</option>
                  </select>
                </div>
              </div>

              {/* Active Filters Summary */}
              {hasActiveAdvancedFilters && (
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className="text-xs text-slate-500">Active filters ({countActiveCriteria()}):</span>
                    {filterState !== "all" && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#0f2744] text-white rounded-full text-xs">
                        {getStateLabel(filterState)}
                        <button onClick={() => { setFilterState("all"); setFilterCity("all"); }} className="hover:text-red-300">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {filterCity !== "all" && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#1e3a5f] text-white rounded-full text-xs">
                        {getCityLabel(filterState, filterCity)}
                        <button onClick={() => setFilterCity("all")} className="hover:text-red-300">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {filterLocation && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#f0f4f8] text-[#0f2744] rounded-full text-xs">
                        <MapPin className="h-3 w-3" />
                        {filterLocation}
                        <button onClick={() => setFilterLocation("")} className="hover:text-red-500">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {(filterBudgetMin || filterBudgetMax) && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#fffdf5] text-[#c9a227] border border-[#d4af37]/30 rounded-full text-xs">
                        ₹{filterBudgetMin || "0"} - ₹{filterBudgetMax || "∞"}
                        <button onClick={() => { setFilterBudgetMin(""); setFilterBudgetMax(""); }} className="hover:text-red-500">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {filterBHK !== "all" && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#f0f4f8] text-[#0f2744] rounded-full text-xs">
                        {filterBHK} BHK
                        <button onClick={() => setFilterBHK("all")} className="hover:text-red-500">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {(filterAreaMin || filterAreaMax) && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#f0f4f8] text-[#0f2744] rounded-full text-xs">
                        {filterAreaMin || "0"} - {filterAreaMax || "∞"} sq.ft
                        <button onClick={() => { setFilterAreaMin(""); setFilterAreaMax(""); }} className="hover:text-red-500">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {filterPossession !== "all" && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                        {POSSESSION_STATUS_OPTIONS.find(o => o.value === filterPossession)?.label}
                        <button onClick={() => setFilterPossession("all")} className="hover:text-red-500">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {filterFurnishing !== "all" && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                        {FURNISHING_OPTIONS.find(o => o.value === filterFurnishing)?.label}
                        <button onClick={() => setFilterFurnishing("all")} className="hover:text-red-500">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {filterRera !== "all" && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                        {filterRera === "yes" ? "RERA Approved" : "Not RERA"}
                        <button onClick={() => setFilterRera("all")} className="hover:text-red-500">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Location Disambiguation Modal */}
          {showLocationPrompt && matchingLocations.length > 1 && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Location found in multiple cities</p>
                  <p className="text-xs text-amber-700 mb-3">Please select a specific city to narrow your search:</p>
                  <div className="flex flex-wrap gap-2">
                    {matchingLocations.map((loc, idx) => (
                      <Button
                        key={idx}
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setFilterState(loc.state);
                          setFilterCity(loc.city);
                          setShowLocationPrompt(false);
                        }}
                        className="text-xs"
                      >
                        {getCityLabel(loc.state, loc.city)}, {getStateLabel(loc.state)}
                      </Button>
                    ))}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowLocationPrompt(false)}
                      className="text-xs text-slate-500"
                    >
                      Skip
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Results Summary */}
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Showing <span className="font-semibold text-[#0f2744]">{filteredProperties.length}</span> of {properties.length} properties
            </p>
            {(searchTerm || filterType !== "all" || filterPurpose !== "all" || hasActiveAdvancedFilters) && (
              <Button
                variant="link"
                size="sm"
                onClick={() => {
                  setSearchTerm("");
                  setFilterType("all");
                  setFilterPurpose("all");
                  clearAdvancedFilters();
                }}
                className="text-[#c9a227] hover:text-[#ae8220] p-0"
              >
                Reset all filters
              </Button>
            )}
          </div>
        </div>

        {/* Properties List Table */}
        {filteredProperties.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-16 text-center">
            <Building2 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg">No properties found</p>
            <p className="text-sm text-slate-400 mt-2">Try adjusting your search criteria</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Desktop Table Header - hidden on mobile */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 bg-gradient-to-r from-[#0f2744] to-[#1e3a5f] text-white font-semibold text-sm">
              <div className="col-span-1">
                <Checkbox
                  checked={selectedProperties.length === filteredProperties.length && filteredProperties.length > 0}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedProperties(filteredProperties.map(p => p.id));
                    } else {
                      setSelectedProperties([]);
                    }
                  }}
                  className="border-white/50 data-[state=checked]:bg-[#c9a227] data-[state=checked]:border-[#c9a227]"
                />
              </div>
              <div className="col-span-3">Property Title</div>
              <div className="col-span-2">Location</div>
              <div className="col-span-2">Price</div>
              <div className="col-span-1">Type</div>
              <div className="col-span-1">Purpose</div>
              <div className="col-span-2 text-center">Actions</div>
            </div>
            
            {/* Mobile Header */}
            <div className="md:hidden flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#0f2744] to-[#1e3a5f] text-white">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={selectedProperties.length === filteredProperties.length && filteredProperties.length > 0}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedProperties(filteredProperties.map(p => p.id));
                    } else {
                      setSelectedProperties([]);
                    }
                  }}
                  className="border-white/50 data-[state=checked]:bg-[#c9a227] data-[state=checked]:border-[#c9a227]"
                />
                <span className="text-sm font-medium">Select All</span>
              </div>
              <span className="text-xs text-white/70">{filteredProperties.length} properties</span>
            </div>
            
            {/* Table Body */}
            <div className="divide-y divide-slate-100">
              {filteredProperties.map((property, index) => (
                <div 
                  key={property.id} 
                  data-testid={`property-row-${property.id}`}
                  className={`cursor-pointer hover:bg-slate-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
                  onClick={() => navigate(`/properties/view/${property.id}`)}
                >
                  {/* Desktop Row */}
                  <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 items-center">
                  {/* Selection Checkbox */}
                  <div className="col-span-1" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedProperties.includes(property.id)}
                      onCheckedChange={() => togglePropertySelection(property.id)}
                      data-testid={`select-property-${property.id}`}
                    />
                  </div>
                  
                  {/* Title */}
                  <div className="col-span-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {!isViewOnlyUser && property.media_files && property.media_files.length > 0 ? (
                          <img 
                            src={property.media_files[0]} 
                            alt={property.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Building2 className="h-6 w-6 text-slate-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-[#0f2744] hover:text-[#c9a227] transition-colors line-clamp-1">
                          {property.title}
                        </p>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {property.property_subtype && (
                            <span className="text-xs text-slate-500 capitalize">{property.property_subtype.replace(/_/g, ' ')}</span>
                          )}
                          {isAdmin && property.created_by_name && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded-full border border-blue-100" data-testid={`created-by-${property.id}`}>
                              <User className="h-2.5 w-2.5" />
                              {property.created_by_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Location */}
                  <div className="col-span-2">
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                      <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                      <span className="line-clamp-1">{property.location}</span>
                    </div>
                  </div>
                  
                  {/* Price */}
                  <div className="col-span-2">
                    <p className="font-bold text-[#c9a227]">₹{property.price.toLocaleString('en-IN')}</p>
                    <p className="text-xs text-slate-500">{property.area} sq ft</p>
                  </div>
                  
                  {/* Type */}
                  <div className="col-span-1">
                    <span className="text-xs px-2 py-1 bg-[#f0f4f8] text-[#1e3a5f] rounded uppercase font-medium">
                      {property.property_type}
                    </span>
                  </div>
                  
                  {/* Purpose */}
                  <div className="col-span-1">
                    <span className="text-xs px-2 py-1 bg-[#fffdf5] text-[#b08d26] border border-[#d4af37]/30 rounded uppercase font-medium">
                      {property.purpose}
                    </span>
                  </div>
                  
                  {/* Actions */}
                  <div className="col-span-2 flex justify-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                    <Button
                      data-testid={`view-property-${property.id}`}
                      onClick={() => navigate(`/properties/view/${property.id}`)}
                      size="sm"
                      variant="outline"
                      className="rounded-lg border-slate-200 hover:border-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white text-xs"
                    >
                      View
                    </Button>
                    {canManageProperties && (
                      <>
                        <Button
                          data-testid={`edit-property-${property.id}`}
                          onClick={() => navigate(`/properties/edit/${property.id}`)}
                          size="sm"
                          variant="outline"
                          className="rounded-lg border-slate-200 hover:border-[#c9a227] hover:bg-[#c9a227] hover:text-white"
                          title="Edit"
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          data-testid={`clone-property-${property.id}`}
                          onClick={() => handleCloneProperty(property.id)}
                          size="sm"
                          variant="outline"
                          className="rounded-lg border-slate-200 hover:border-green-500 hover:bg-green-500 hover:text-white"
                          title="Clone property"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                        {canDeleteProperties && (
                        <Button
                          data-testid={`delete-property-${property.id}`}
                          onClick={() => setDeleteId(property.id)}
                          size="sm"
                          variant="outline"
                          className="rounded-lg border-red-200 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600"
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                        )}
                      </>
                    )}
                  </div>
                  </div>
                  
                  {/* Mobile Card Layout */}
                  <div className="md:hidden p-4">
                    <div className="flex items-start gap-3">
                      <div onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedProperties.includes(property.id)}
                          onCheckedChange={() => togglePropertySelection(property.id)}
                          data-testid={`select-property-mobile-${property.id}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-14 h-14 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {!isViewOnlyUser && property.media_files && property.media_files.length > 0 ? (
                              <img src={property.media_files[0]} alt={property.title} className="w-full h-full object-cover" />
                            ) : (
                              <Building2 className="h-6 w-6 text-slate-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-[#0f2744] text-sm line-clamp-1">{property.title}</p>
                            <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                              <MapPin className="h-3 w-3 flex-shrink-0" />
                              <span className="line-clamp-1">{property.location}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <p className="font-bold text-[#c9a227] text-sm">₹{property.price.toLocaleString('en-IN')}</p>
                          <div className="flex gap-1">
                            <span className="text-[10px] px-1.5 py-0.5 bg-[#f0f4f8] text-[#1e3a5f] rounded uppercase font-medium">{property.property_type}</span>
                            <span className="text-[10px] px-1.5 py-0.5 bg-[#fffdf5] text-[#b08d26] border border-[#d4af37]/30 rounded uppercase font-medium">{property.purpose}</span>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                          <Button
                            data-testid={`view-property-mobile-${property.id}`}
                            onClick={() => navigate(`/properties/view/${property.id}`)}
                            size="sm"
                            variant="outline"
                            className="flex-1 rounded-lg border-slate-200 hover:border-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white text-xs h-8"
                          >
                            View
                          </Button>
                          {canManageProperties && (
                            <>
                              <Button
                                onClick={() => navigate(`/properties/edit/${property.id}`)}
                                size="sm"
                                variant="outline"
                                className="rounded-lg border-slate-200 hover:border-[#c9a227] hover:bg-[#c9a227] hover:text-white h-8"
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                              {canDeleteProperties && (
                              <Button
                                onClick={() => setDeleteId(property.id)}
                                size="sm"
                                variant="outline"
                                className="rounded-lg border-red-200 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600 h-8"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Property</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this property? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-[#0f2744] hover:bg-[#1e3a5f]">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Floating Selection Bar */}
        {selectedProperties.length > 0 && (
          <div className="fixed bottom-4 left-4 right-4 md:left-auto md:bottom-6 md:right-6 z-50 animate-in slide-in-from-bottom-5">
            <div className="bg-white rounded-xl shadow-2xl border border-slate-200 p-3 md:p-4">
              <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
                <div>
                  <p className="text-sm font-medium text-[#1e3a5f]">
                    {selectedProperties.length} {selectedProperties.length === 1 ? 'property' : 'properties'} selected
                  </p>
                  <p className="text-xs text-slate-500">
                    {selectedProperties.length === 1 ? 'View details or send inquiry' : 'Ready to inquire?'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedProperties([])}
                    className="rounded-lg"
                  >
                    Clear
                  </Button>
                  {selectedProperties.length === 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/properties/view/${selectedProperties[0]}`)}
                      className="rounded-lg"
                      data-testid="view-selected-property"
                    >
                      View Details
                    </Button>
                  )}
                  <Button
                    onClick={() => setShowInquiryModal(true)}
                    className="bg-gradient-to-r from-[#1e3a5f] to-[#162c46] text-white rounded-lg"
                    data-testid="open-inquiry-modal"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Send Inquiry
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Inquiry Modal */}
        {showInquiryModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-[#1e3a5f]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Property Inquiry
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                      Interested in {selectedProperties.length} {selectedProperties.length === 1 ? 'property' : 'properties'}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowInquiryModal(false)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Selected Properties Preview */}
              <div className="p-4 bg-[#f0f4f8] border-b border-slate-200">
                <p className="text-xs text-[#1e3a5f] uppercase font-medium mb-2">Selected Properties</p>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {getSelectedPropertyDetails().map(prop => (
                    <div key={prop.id} className="flex items-center justify-between bg-white p-2 rounded-lg border border-slate-200">
                      <div>
                        <p className="text-sm font-medium text-[#1e3a5f]">{prop.title}</p>
                        <p className="text-xs text-slate-500">{prop.location} • ₹{prop.price.toLocaleString('en-IN')}</p>
                      </div>
                      <button
                        onClick={() => togglePropertySelection(prop.id)}
                        className="text-slate-400 hover:text-[#0f2744]"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <form onSubmit={handleInquirySubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="full_name" className="text-[#1e3a5f] font-semibold">Full Name *</Label>
                    <Input
                      id="full_name"
                      value={inquiryForm.full_name}
                      onChange={(e) => setInquiryForm(prev => ({ ...prev, full_name: e.target.value }))}
                      placeholder="Your full name"
                      required
                      className="mt-1 rounded-lg"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-[#1e3a5f] font-semibold">Phone Number *</Label>
                    <Input
                      id="phone"
                      value={inquiryForm.phone}
                      onChange={(e) => setInquiryForm(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+91 98765 43210"
                      required
                      className="mt-1 rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#1e3a5f] font-semibold">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={inquiryForm.email}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="your@email.com"
                    required
                    className="mt-1 rounded-lg"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="budget_min" className="text-[#1e3a5f] font-semibold">Budget Min (₹)</Label>
                    <Input
                      id="budget_min"
                      type="number"
                      value={inquiryForm.budget_min}
                      onChange={(e) => setInquiryForm(prev => ({ ...prev, budget_min: e.target.value }))}
                      placeholder="50,00,000"
                      className="mt-1 rounded-lg"
                    />
                  </div>
                  <div>
                    <Label htmlFor="budget_max" className="text-[#1e3a5f] font-semibold">Budget Max (₹)</Label>
                    <Input
                      id="budget_max"
                      type="number"
                      value={inquiryForm.budget_max}
                      onChange={(e) => setInquiryForm(prev => ({ ...prev, budget_max: e.target.value }))}
                      placeholder="1,00,00,000"
                      className="mt-1 rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-[#1e3a5f] font-semibold">Preferred Contact Method</Label>
                  <div className="flex gap-4 mt-2">
                    {[
                      { value: 'phone', icon: Phone, label: 'Phone' },
                      { value: 'email', icon: Mail, label: 'Email' },
                      { value: 'whatsapp', icon: MessageSquare, label: 'WhatsApp' }
                    ].map(({ value, icon: Icon, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setInquiryForm(prev => ({ ...prev, preferred_contact: value }))}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                          inquiryForm.preferred_contact === value
                            ? 'border-[#d4af37] bg-[#fffdf5] text-[#1e3a5f]'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="text-sm">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-[#1e3a5f] font-semibold">Message (Optional)</Label>
                  <Textarea
                    id="message"
                    value={inquiryForm.message}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Tell us more about what you're looking for..."
                    rows={3}
                    className="mt-1 rounded-lg"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowInquiryModal(false)}
                    className="flex-1 rounded-lg"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={inquiryLoading}
                    className="flex-1 bg-gradient-to-r from-[#1e3a5f] to-[#162c46] text-white rounded-lg"
                    data-testid="submit-inquiry"
                  >
                    {inquiryLoading ? (
                      <div className="spinner mx-auto" style={{ width: '20px', height: '20px', borderWidth: '2px' }} />
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Submit Inquiry
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
